import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import callApi from '../../../utils/callApi';
import checkCondition from '../../../utils/checkCondition';
import { LOADING_TYPES, useCheckoutState } from '../../../utils/checkoutState';
import { config, getAssetUrl } from '../../../utils/config';
import {
  ConditionValues,
  DynamicFieldType,
  DynamicFieldWithKeyProps,
  useDynamicFields
} from '../../../utils/dynamicFields';
import getValidator, { Validation } from '../../../utils/getValidator';
import isMultiCountry from '../../../utils/isMultiCountry';
import { pushErrorTrackingEventFromErrorObject } from '../../../utils/tracking';
import Button from '../../atoms/Button';
import getBirthDateValidator from '../../atoms/DateInput/getBirthDateValidator';
import LoadingIndicator from '../../atoms/LoadingIndicator';
import MainHeadline from '../../atoms/MainHeadline';
import Options from '../../atoms/Options';
import getPhoneNumberValidator, { PhoneNumberValidation } from '../../atoms/PhoneInput/getPhoneNumberValidator';
import SubHeadline from '../../atoms/SubHeadline';
import AddressOverview from '../../molecules/AddressOverview';
import DynamicFieldArea from '../../molecules/DynamicFieldArea';
import { showDefaultError, showNotification } from '../../molecules/Notifications';
import additionalPaymentConfig from '../../molecules/PaymentOptions/additionalPaymentConfig';
import PriceSummary from '../../molecules/PriceSummary';
import Basket from '../../organisms/Basket';
import { useAmazonPaymentAddressChange } from '../AmazonPayment';
import { useKlarnaPayment } from '../KlarnaPayment';
import './style.scss';

type ReviewProps = {
  showBasket: boolean;
  isPpx: boolean;
  isExpressCheckoutReviewPage: boolean;
  isAmazonPayReviewPage: boolean;
};

const REVIEW_FIELDS = ['phoneNumber', 'logisticsPhoneNumber', 'birthDate', 'taxId'];

const Review = ({ showBasket, isPpx, isExpressCheckoutReviewPage, isAmazonPayReviewPage }: ReviewProps) => {
  const { formatMessage } = useIntl();
  const { submitDynamicFieldArea } = useDynamicFields();
  const { finalizeKlarnaPayment } = useKlarnaPayment();
  const onAddressChangeClick = useAmazonPaymentAddressChange();
  const { confirmOrder, setPollingEnabled, isLoading, setLoading, checkoutState } = useCheckoutState();

  const {
    customer,
    orderState: { addresses, basket, payment, shipping, taxId }
  } = checkoutState;
  const hasShippingAddress = !!addresses?.shipping;
  const selectedPaymentOptionKey = payment?.selected?.[0]?.paymentOptionKey;
  const basketTotal = basket.total.costCapture ?? basket.total.costWithTaxWithVouchersWithServiceCostsWithGiftCard;
  const basketShippingCost = basket.total.costShippingServiceCosts;

  const alwaysShowReviewPage = config.features.review.showAlways;
  const country = isMultiCountry(config.country) ? undefined : config.country;
  const currentUrl = new URL(window.location.href);

  const [isValidating, setIsValidating] = useState(true);
  const [invalidFields, setInvalidFields] = useState<DynamicFieldWithKeyProps[]>([]);
  const [isEditingAddress, setEditingAddress] = useState(!hasShippingAddress);

  const selectedPayment = additionalPaymentConfig[selectedPaymentOptionKey];

  const selectedPaymentMethodOption = selectedPaymentOptionKey && {
    key: selectedPaymentOptionKey,
    label: formatMessage({
      id: `payment.paymentMethod.options.${selectedPayment.i18nKey}`
    }),
    defaultIcon: getAssetUrl(`payment/${selectedPayment.i18nKey}.svg`),
    ...(!config.paymentOptions.hidePaymentIcons &&
      selectedPayment.iconKeys && {
        defaultIcons: selectedPayment.iconKeys?.map(i => getAssetUrl(`payment/${i}.svg`))
      })
  };

  useEffect(() => {
    if (isAmazonPayReviewPage) {
      return;
    }

    const invalidDynamicFields = validateFields();
    if (invalidDynamicFields.length) {
      setInvalidFields(invalidDynamicFields);

      // Override config for review fields based on invalid fields that need to be filled
      config.dynamicFields.review = invalidDynamicFields.reduce(
        (config, field) => ({
          ...config,
          [field.key]: { enabled: true }
        }),
        {}
      );

      showNotification({
        level: 'error',
        text: formatMessage({ id: 'review.notifications.missingInfo' }),
        autoClose: false
      });
      setIsValidating(false);
      return;
    }

    if (!hasShippingAddress) {
      showNotification({
        level: 'error',
        text: formatMessage({ id: 'review.notifications.missingInfo' }),
        autoClose: false
      });
      setIsValidating(false);
      return;
    }

    if (!alwaysShowReviewPage) {
      (async () => {
        try {
          await confirmOrder({
            paymentOptionKey: selectedPaymentOptionKey!,
            currentUrl: currentUrl.toString()
          });
        } catch (e: any) {
          console.error(e);
          showDefaultError();
          pushErrorTrackingEventFromErrorObject(e, 'confirm_order_paypal_instant');
          setIsValidating(false);
        }
      })();
    } else {
      setIsValidating(false);
    }
  }, []);

  const initialValues = {
    phoneNumber: customer?.data.phone?.plainText ?? '',
    logisticsPhoneNumber: shipping?.logisticsContact?.plainText ?? '',
    birthDate: customer?.data.birthDate ?? '',
    taxId: taxId ?? customer?.data.customData?.taxId ?? ''
  };

  const validatorMap = {
    phoneNumber: (props: PhoneNumberValidation) => getPhoneNumberValidator({ ...props, country }),
    logisticsPhoneNumber: (props: PhoneNumberValidation) => getPhoneNumberValidator({ ...props, country }),
    birthDate: getBirthDateValidator,
    taxId: (props: Omit<Validation, 'maxLength'>) => getValidator({ format: 'default', ...props })
  };

  const validateFields = () => {
    const fields: DynamicFieldWithKeyProps[] = [];
    const requiredFields = Object.entries(config.dynamicFields).flatMap(([_areaKey, areaFields]) =>
      Object.entries(areaFields)
        .filter(([fieldKey, { required, conditions }]) => {
          if (!REVIEW_FIELDS.includes(fieldKey)) {
            return false;
          }

          if (!required && !conditions?.required) {
            return false;
          }

          if (conditions) {
            const conditionValues: ConditionValues = {
              carrierGroupKey: shipping?.selected?.[0].carrierGroupKey,
              shippingOptionKey: shipping?.selected?.[0].shippingOptionKey,
              paymentOptionKey: selectedPaymentOptionKey,
              shippingCountry: addresses?.shipping?.country?.iso2Code,
              billingCountry: addresses?.billing?.country?.iso2Code,
              basketTotal: basket.total.costCapture ?? basket.total.costWithTaxWithVouchersWithServiceCostsWithGiftCard
            };

            if (!required && conditions.required) {
              return checkCondition(conditions?.required, conditionValues);
            }

            if (required && conditions.enabled) {
              return checkCondition(conditions?.enabled, conditionValues);
            }
          }

          return true;
        })
        .map(([fieldKey, rules]) => ({ [fieldKey]: rules }))
    );

    requiredFields.forEach(field =>
      Object.entries(field).forEach(([fieldKey, fieldConfig]) => {
        if (fields.some(existingField => existingField.key === fieldKey)) {
          return;
        }

        const fieldToValidate = {
          key: fieldKey,
          type: fieldKey as DynamicFieldType,
          name: fieldKey,
          dataTestId: `review-${fieldKey}`,
          ...fieldConfig,
          ...(fieldConfig.conditions && { required: true })
        };

        const validatorFunction = validatorMap[fieldKey](fieldToValidate);

        if (validatorFunction(initialValues[fieldKey])) {
          fields.push(fieldToValidate);
        }
      })
    );

    return fields;
  };

  const payNowAmazon = async () => {
    setPollingEnabled(false);
    setLoading(LOADING_TYPES.paymentSubmit);
    try {
      await confirmOrder({
        paymentOptionKey: 'amazon_pay',
        currentUrl: currentUrl.toString()
      });
    } catch (e) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'confirm_order_amazon_pay');
      setPollingEnabled(false);
    }
    setLoading(LOADING_TYPES.off);
  };

  const onSubmit = async (values: typeof initialValues) => {
    setLoading(LOADING_TYPES.paymentSubmit);
    let hasRedirect = false;

    try {
      await submitDynamicFieldArea({ area: 'review' }, values);
      if (isExpressCheckoutReviewPage && config.transactionId) {
        await callApi({
          flow: 'express',
          method: 'PUT',
          endpoint: `/transactions/${config.transactionId}`,
          payload: { providerData: {} }
        });
      }

      if (selectedPaymentOptionKey === 'klarna_paynow') {
        await finalizeKlarnaPayment();
      }

      const result = await confirmOrder({
        paymentOptionKey: selectedPaymentOptionKey!,
        currentUrl: currentUrl.toString()
      });
      hasRedirect = result.hasRedirect;
    } catch (e: any) {
      console.error(e);
      showDefaultError();
      pushErrorTrackingEventFromErrorObject(e, 'confirm_order_paypal_instant');
      setPollingEnabled(false);
    }

    if (!hasRedirect) {
      setPollingEnabled(true);
    }
    setLoading(LOADING_TYPES.off);
  };

  const isPayNowDisabled = (values: typeof initialValues, isSubmitting: boolean) =>
    isSubmitting ||
    !hasShippingAddress ||
    isEditingAddress ||
    !invalidFields.every(item => {
      const key = item.key;
      const validatorFunction = validatorMap[key](item);
      return !validatorFunction(values[key]);
    });

  if (!isAmazonPayReviewPage && isValidating) {
    return (
      <div>
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ submitForm, values, isSubmitting }) => (
        <div className="review">
          <MainHeadline>
            <FormattedMessage id="review.headline" />
          </MainHeadline>
          <SubHeadline>
            <FormattedMessage id="address.shippingAddress" />
          </SubHeadline>
          <AddressOverview
            addressType="shipping"
            showAddressForm={isPpx || isExpressCheckoutReviewPage}
            onChangeClick={onAddressChangeClick}
            isEditingAddress={isEditingAddress}
            setEditingAddress={setEditingAddress}
          />
          {isAmazonPayReviewPage && (
            <>
              <SubHeadline>
                <FormattedMessage id="address.billingAddress" />
              </SubHeadline>
              <AddressOverview addressType="billing" onChangeClick={onAddressChangeClick} />
            </>
          )}
          {selectedPaymentMethodOption && (
            <>
              <SubHeadline>
                <FormattedMessage id="payment.headline" />
              </SubHeadline>
              <Options
                isLoading={isLoading}
                isVertical
                options={[selectedPaymentMethodOption]}
                value={selectedPaymentOptionKey}
                dataTestIdPrefix="paymentOptions"
              />
            </>
          )}
          {!!invalidFields.length && (
            <div className="invalid-fields">
              <DynamicFieldArea area="review" reviewFields={invalidFields} />
            </div>
          )}
          {showBasket && <Basket />}
          <PriceSummary className="price-summary__box" total={basketTotal} shippingCost={basketShippingCost} />
          <div className="navigation-section">
            <Button
              type="button"
              onClick={isPpx || isExpressCheckoutReviewPage ? submitForm : payNowAmazon}
              className="navigation-section__next-step"
              isLoading={isLoading === LOADING_TYPES.paymentSubmit}
              disabled={isPayNowDisabled(values, isSubmitting)}
              data-test-id="navigation-next-step"
            >
              <FormattedMessage id="payment.payNow" />
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
};

export default Review;
